// src/GlobalPages/TrophyRoom.js
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  LinearProgress,
  Tab,
  Tabs,
  Fade,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon,
  PersonAdd as ReferralIcon,
  MilitaryTech as MilitaryTechIcon,
  CheckCircle as CheckCircleIcon,
  DoNotDisturb as DoNotDisturbIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import client from '../API';
import useAuth from '../Hooks/use-auth';
import { useCreatorAuth } from '../Hooks/creator-use-auth';
import { ThemeProvider, createTheme } from '@mui/material/styles';

// Component imports
import { TopPerformersPodium } from './trophyRoomComponents';
import { LeaderboardCard } from './trophyRoomComponents';
import { MyReferrals } from './trophyRoomComponents';
import { TabPanel } from './trophyRoomComponents';

// Force light theme for Trophy Room
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
    warning: {
      main: '#f57c00',
      light: '#ffb74d',
      dark: '#e65100',
    },
    success: {
      main: '#388e3c',
      light: '#66bb6a',
      dark: '#2e7d32',
    },
    info: {
      main: '#0288d1',
      light: '#03dac6',
      dark: '#01579b',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }
      }
    }
  }
});

// Icon mapping for achievements
const achievementIconMap = {
  Star: <StarIcon sx={{ color: "#FFD700" }} />,
  EmojiEvents: <TrophyIcon sx={{ color: "#FFD700" }} />,
  MilitaryTech: <MilitaryTechIcon sx={{ color: "#FFD700" }} />,
};

const TAB_TO_INDEX = {
  leaderboard: 0,
  referrals: 1,
  achievements: 2,
};
const INDEX_TO_TAB = ['leaderboard', 'referrals', 'achievements'];

export default function TrophyRoom() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');

  // Read initial tab from URL (default to leaderboard)
  const initialTab = (() => {
    const t = (searchParams.get('tab') || '').toLowerCase();
    return TAB_TO_INDEX[t] ?? 0;
  })();

  const [tabValue, setTabValue] = useState(initialTab);

  // Keep local state in sync when the URL changes (back/forward buttons)
  useEffect(() => {
    const t = (searchParams.get('tab') || '').toLowerCase();
    const idx = TAB_TO_INDEX[t] ?? 0;
    if (idx !== tabValue) setTabValue(idx);
  }, [searchParams]);

  // Ensure URL always has a tab param
  useEffect(() => {
    if (!searchParams.get('tab')) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', INDEX_TO_TAB[tabValue]);
      setSearchParams(next, { replace: true });
    }
  }, []);

  const { isAuthorized: isUserAuthorized, user: regularUser, getCurrrentUser } = useAuth();
  const { isAuthorized: isCreatorAuthorized, user: creatorUser, creatorToken, isInitialized } = useCreatorAuth();
  
  // Enhanced user ID resolution
  const currentUserId = useMemo(() => {
    if (isUserAuthorized && regularUser?.globalUserId) {
      return regularUser.globalUserId;
    }
    if (isCreatorAuthorized && creatorUser?.globalUserId) {
      return creatorUser.globalUserId;
    }
    return null;
  }, [isUserAuthorized, isCreatorAuthorized, regularUser, creatorUser]);

  // Get user identifier for achievements API
  const userIdentifier = useMemo(() => {
    if (isCreatorAuthorized && creatorToken?.creator_user?.username) {
      return creatorToken.creator_user.username;
    } else if (isUserAuthorized) {
      const user = getCurrrentUser();
      if (user?.id) return user.id;
    }
    return null;
  }, [isCreatorAuthorized, isUserAuthorized, creatorToken, getCurrrentUser]);

  // Load user settings for font preferences
  const { data: userSettings } = useQuery(
    ["userSettingsTrophyRoom"],
    () => client.users.fetchUserSettings(),
    { 
      enabled: isUserAuthorized || isCreatorAuthorized,
      staleTime: 1000 * 60 * 60 * 12,
      refetchOnWindowFocus: false,
    }
  );

  // Compute font family
  const fontPreference = userSettings?.font_preference || "default";
  const userFontFamily = {
    roboto: '"Roboto", "Helvetica", "Arial", sans-serif',
    "open-sans": '"Open Sans", sans-serif',
    montserrat: '"Montserrat", sans-serif',
    default: 'Inter, Arial, sans-serif'
  }[fontPreference];

  // Wait for creator auth to initialize
  if (!isInitialized) {
    return (
      <ThemeProvider theme={lightTheme}>
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4, md: 6 } }}>
          <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
            <Box textAlign="center">
              <LinearProgress sx={{ 
                width: { xs: 280, sm: 400 }, 
                mb: 3, 
                height: { xs: 6, sm: 8 }, 
                borderRadius: 4 
              }} />
              <Typography color="text.secondary" variant={isMobile ? "body1" : "h6"}>
                Initializing...
              </Typography>
            </Box>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  const { data: leaderboardData, isLoading: leaderboardLoading, error: leaderboardError } = useQuery(
    ['blitzbolts-leaderboard'],
    () => client.blitzbolts.getLeaderboard(),
    {
      staleTime: 1000 * 60 * 5,
      refetchInterval: 1000 * 60 * 10,
    }
  );

  // Fetch achievements for the current user
  const { data: blitzData, isLoading: achievementsLoading } = useQuery(
    ["blitzbolts-for-user-trophy", userIdentifier],
    () => client.blitzbolts.listForUser(userIdentifier, { scope: "self" }),
    { 
      enabled: Boolean(userIdentifier), 
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  // Extract achievements from response
  const achievements = useMemo(() => {
    if (Array.isArray(blitzData)) return blitzData;
    if (blitzData?.bolts && Array.isArray(blitzData.bolts)) return blitzData.bolts;
    if (blitzData?.data?.bolts && Array.isArray(blitzData.data.bolts)) return blitzData.data.bolts;
    return [];
  }, [blitzData]);

  const achievedCount = useMemo(
    () => achievements.filter(a => a?.attainment?.achieved).length,
    [achievements]
  );

  const handleTabChange = (_event, newValue) => {
    setTabValue(newValue);
    const next = new URLSearchParams(searchParams);
    next.set('tab', INDEX_TO_TAB[newValue]);
    setSearchParams(next);
  };

  const isLoading = leaderboardLoading || (tabValue === 2 && achievementsLoading);

  if (isLoading && !leaderboardData) {
    return (
      <ThemeProvider theme={lightTheme}>
        <Container 
          maxWidth="lg" 
          sx={{ 
            py: { xs: 2, sm: 4, md: 6 },
            fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
            <Box textAlign="center">
              <LinearProgress sx={{ 
                width: { xs: 280, sm: 400 }, 
                mb: 3, 
                height: { xs: 6, sm: 8 }, 
                borderRadius: 4 
              }} />
              <Typography color="text.secondary" variant={isMobile ? "body1" : "h6"}>
                Loading trophy room...
              </Typography>
            </Box>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (leaderboardError && tabValue === 0) {
    return (
      <ThemeProvider theme={lightTheme}>
        <Container 
          maxWidth="lg" 
          sx={{ 
            py: { xs: 2, sm: 4, md: 6 },
            fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit'
          }}
        >
          <Box textAlign="center" py={{ xs: 6, sm: 10 }}>
            <TrophyIcon sx={{ 
              fontSize: { xs: 60, sm: 80 }, 
              color: 'text.disabled', 
              mb: { xs: 2, sm: 3 } 
            }} />
            <Typography variant={isMobile ? "h5" : "h4"} color="text.secondary" gutterBottom>
              Failed to load trophy room
            </Typography>
            <Typography color="text.secondary" variant="body1">
              Please try again later.
            </Typography>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  const { leaderboard = [] } = leaderboardData || {};
  const topPerformers = leaderboard.slice(0, 25);

  return (
    <ThemeProvider theme={lightTheme}>
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: { xs: 2, sm: 3, md: 4 },
          fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit'
        }}
      >
        <Fade in timeout={600}>
          <Box textAlign="center" mb={{ xs: 4, sm: 6 }} px={{ xs: 1, sm: 0 }}>
            <Typography 
              variant={isMobile ? "h3" : isTablet ? "h2" : "h1"} 
              component="h1" 
              gutterBottom 
              display="flex" 
              alignItems="center" 
              justifyContent="center" 
              gap={{ xs: 2, sm: 3 }}
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(45deg, #FFD700, #FFA000, #FF8F00)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row',
                fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit'
              }}
            >
              <TrophyIcon sx={{ 
                fontSize: { xs: 40, sm: 50, md: 60 }, 
                color: '#FFD700' 
              }} />
              Trophy Room
            </Typography>
            <Typography 
              variant={isMobile ? "body1" : "h5"} 
              color="text.secondary" 
              sx={{ 
                maxWidth: { xs: '100%', sm: 600 }, 
                mx: 'auto',
                px: { xs: 2, sm: 0 },
                fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit'
              }}
            >
              Celebrating achievements and recognizing top performers across the platform
            </Typography>
          </Box>
        </Fade>

        <TopPerformersPodium leaderboard={leaderboard} />

        <Paper sx={{ 
          mb: { xs: 3, sm: 4 }, 
          borderRadius: { xs: 2, sm: 3 }, 
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant={isMobile ? "fullWidth" : "standard"}
            centered={!isMobile}
            sx={{
              '& .MuiTab-root': {
                py: { xs: 1.5, sm: 2 },
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: { xs: 56, sm: 72 },
                fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit',
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08), rgba(25, 118, 210, 0.04))'
                }
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            <Tab 
              label={isMobile ? "Leaders" : "Leaderboard"}
              icon={<TrendingIcon />}
              iconPosition={isMobile ? "top" : "start"}
            />
            <Tab 
              label={isMobile ? "Referrals" : "My Referrals"}
              icon={<ReferralIcon />}
              iconPosition={isMobile ? "top" : "start"}
            />
            <Tab 
              label={isMobile ? "Awards" : "My Achievements"}
              icon={<TrophyIcon />}
              iconPosition={isMobile ? "top" : "start"}
            />
          </Tabs>
        </Paper>

        <TabPanel value={tabValue} index={0}>
          <Box mb={{ xs: 3, sm: 4 }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              gutterBottom 
              display="flex" 
              alignItems="center" 
              gap={2}
              sx={{
                flexDirection: isMobile ? 'column' : 'row',
                textAlign: isMobile ? 'center' : 'left',
                fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit'
              }}
            >
              <TrendingIcon sx={{ fontSize: { xs: 30, sm: 40 } }} />
              Top 25 Performers
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              textAlign={isMobile ? "center" : "left"}
              sx={{ fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit' }}
            >
              Ranked by total points earned from achievements
            </Typography>
          </Box>

          {topPerformers.length === 0 ? (
            <Paper sx={{ 
              p: { xs: 4, sm: 6, md: 8 }, 
              textAlign: 'center', 
              borderRadius: { xs: 2, sm: 3 },
              background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.02), rgba(25, 118, 210, 0.01))'
            }}>
              <TrophyIcon sx={{ 
                fontSize: { xs: 60, sm: 80 }, 
                color: 'text.disabled', 
                mb: { xs: 2, sm: 3 } 
              }} />
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                color="text.secondary" 
                gutterBottom
                sx={{ fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit' }}
              >
                No achievements yet!
              </Typography>
              <Typography 
                color="text.secondary" 
                variant="body1"
                sx={{ fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit' }}
              >
                Be the first to earn some BlitzBolts and claim your spot on the leaderboard.
              </Typography>
            </Paper>
          ) : (
            <Box>
              {topPerformers.map((user, index) => (
                <LeaderboardCard
                  key={user.global_user_id}
                  user={user}
                  rank={index + 1}
                  currentUserId={currentUserId}
                  index={index}
                />
              ))}
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <MyReferrals />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box mb={{ xs: 3, sm: 4 }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              gutterBottom 
              display="flex" 
              alignItems="center" 
              gap={2}
              sx={{
                flexDirection: isMobile ? 'column' : 'row',
                textAlign: isMobile ? 'center' : 'left',
                fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit'
              }}
            >
              <TrophyIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: '#FFD700' }} />
              My Achievements
              {achievedCount > 0 && (
                <Chip 
                  label={`${achievedCount}/${achievements.length}`} 
                  size={isMobile ? "small" : "medium"}
                  color="primary"
                  sx={{ fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit' }}
                />
              )}
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              textAlign={isMobile ? "center" : "left"}
              sx={{ fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit' }}
            >
              Track your progress and unlock rewards by completing challenges
            </Typography>
          </Box>

          {achievementsLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <LinearProgress sx={{ width: { xs: 280, sm: 400 } }} />
            </Box>
          ) : achievements.length === 0 ? (
            <Paper sx={{ 
              p: { xs: 4, sm: 6, md: 8 }, 
              textAlign: 'center', 
              borderRadius: { xs: 2, sm: 3 },
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.02), rgba(255, 215, 0, 0.01))'
            }}>
              <TrophyIcon sx={{ 
                fontSize: { xs: 60, sm: 80 }, 
                color: 'text.disabled', 
                mb: { xs: 2, sm: 3 } 
              }} />
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                color="text.secondary" 
                gutterBottom
                sx={{ fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit' }}
              >
                No achievements yet
              </Typography>
              <Typography 
                color="text.secondary" 
                variant="body1"
                sx={{ fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit' }}
              >
                Start completing challenges to earn BlitzBolts!
              </Typography>
            </Paper>
          ) : (
            <List sx={{ p: 0 }}>
              {achievements.map((achievement) => {
                const achieved = Boolean(achievement?.attainment?.achieved);
                const icon = achievementIconMap[achievement?.icon_name] || achievementIconMap.Star;
                
                return (
                  <ListItem
                    key={achievement?.id ?? `achievement-${achievement?.title}`}
                    sx={{
                      border: `1px solid ${alpha('#000', 0.1)}`,
                      borderRadius: 2,
                      mb: 2,
                      p: { xs: 2, sm: 3 },
                      opacity: achieved ? 1 : 0.6,
                      background: achieved 
                        ? alpha('#4caf50', 0.05)
                        : alpha('#9e9e9e', 0.03),
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                      fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: { xs: 48, sm: 56 } }}>
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: 'wrap' }}>
                          <Typography 
                            variant={isMobile ? "subtitle1" : "h6"}
                            sx={{ 
                              fontWeight: 600,
                              fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit',
                            }}
                          >
                            {achievement?.title ?? "Untitled Achievement"}
                          </Typography>
                          {achieved ? (
                            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: { xs: 18, sm: 20 } }} />
                          ) : (
                            <DoNotDisturbIcon sx={{ color: '#9e9e9e', fontSize: { xs: 18, sm: 20 } }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mb: 1.5,
                              fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit',
                            }}
                          >
                            {achievement?.description ?? ""}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                              label={`${Number(achievement?.points) || 0} pts`}
                              size="small"
                              variant={achieved ? "filled" : "outlined"}
                              color={achieved ? "success" : "default"}
                              sx={{ 
                                fontWeight: 600,
                                fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit'
                              }}
                            />
                            {achieved && achievement?.attainment?.achieved_at && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  fontFamily: (isUserAuthorized || isCreatorAuthorized) ? userFontFamily : 'inherit'
                                }}
                              >
                                Earned: {new Date(achievement.attainment.achieved_at).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </TabPanel>
      </Container>
    </ThemeProvider>
  );
}